/**
 * **THE GLYPH VOCABULARY — the names content may refer to, and the drawings `@kk/ui` must cover.**
 *
 * A tile id, a badge `art` key and an illustrator's filename are the same string (`questArt.ts`'s naming
 * convention: `<key>.svg`, lowercase kebab-case, name the SUBJECT). Three packages need to agree on it: the
 * API validates what an author submits, the portal offers it in a picker, and `@kk/ui` draws it.
 *
 * **It lives HERE rather than in `@kk/ui` because the API must not depend on the design system to know what a
 * legal key is** — and because `@kk/ui` already depends on this package, so the drawings can be bound to this
 * list without a cycle. `glyphs` is declared `satisfies Record<GlyphKey, Glyph>`, which makes the covering a
 * COMPILE error in both directions: a key here with no drawing fails, and a drawing whose name is not here
 * fails. One list, enforced by the type checker rather than by a test that has to be remembered.
 *
 * **Adding a glyph is two edits in one commit:** the key here, the path data there.
 */
export const GLYPH_KEYS = [
  'check',
  'x',
  'plus',
  'minus',
  'arrow-left',
  'arrow-right',
  'triangle-up',
  'chevron-right',
  'camera',
  'bell',
  'eye',
  'eye-off',
  'speech',
  'door',
  'envelope',
  'lock',
  'gift',
  'flame',
  'chart',
  /*
   * **A SCORE THAT DROPS, and the undo of a Cub's tap** (UI/UX audit, 23 Oct). `chart` goes UP, and the contracts'
   * "your score drops a little" sat beside it: for a Cub the picture is the message. `chart-down` is lifted from
   * New Build board 07-7's own markup. `undo` is the 2-minute Undo on a Cub's chore tile (DECISIONS 93.6); the board
   * draws none, so it is the counter-clockwise arrow from the same Feather set as the rest.
   */
  'chart-down',
  'undo',
  'alert',
  'clock',
  'calendar',
  'home',
  'list',
  'star',
  'people',
  'wallet',
  'card',
  'medal',
  'speaker',
  'ticket',
  'face-id',
  'moneybox',
  'game',
  'meal',
  'ice-cream',
  'bike',
  'ball',
  'blocks',
  // C26's three default savings-goal choices are Roblox, Minecraft and Lego; these are the first two, and
  // `blocks` above is the third. The tile NAMES are the family's and editable on P31 — these are the pictures,
  // which is why they are subjects (a controller, a cube) rather than products. The board: "no game's logo is
  // ever drawn". `gamepad` is a two-grip controller, distinct from `game`, the handheld C06 already uses.
  'gamepad',
  'cube',
  'kite',
  'apple',
  'google',
  'broom',
  'signpost',
  'key',
  'shield',
  'bank',
  'coin-return',
  'seedling-pot',
  // brief 05 (New Build board 05-2 "How?", 05a Oops row), 14 Oct
  'question',
  'sad',
  'kai',
  'pip',
  /*
   * **THE CHORE PICTURES (P12b, board 24 Sep)** — the set a parent picks from when they make a chore, and the
   * set a pre-reader reads a chore BY. Eight, lifted from the artboard's own markup with the keys Evan named.
   *
   *  · `bed` and `bowl` are the two drawings C09's chore tiles already use, so the screen and the picker
   *    cannot show a child two different pictures for one chore.
   *  · `pencil` is ALSO the edit pencil on P23 and P24 — the board draws the identical path in all three
   *    places, and there is one drawing per subject here (see `GLYPH_ALIAS`), so it is one key.
   *
   * **⚠ THIS LIST IS WHAT A CHORE PICTURE IS VALIDATED AGAINST**, so widening it is the API session's business
   * too: eight added, none removed, none renamed, so nothing already stored stops being legal.
   */
  'bed',
  'bowl',
  'backpack',
  'book',
  'pencil',
  'toothbrush',
  'trash',
  'shirt',
  /*
   * **BRIEF 03's SIX CATEGORY ICONS, as the board draws them** (requirement 7: *"Each category has an icon for
   * Cub kids and a read-aloud name — six new strings, each needing a Spanish draft, and six new icons."*).
   *
   * **Four, not six**, because this list holds one drawing per SUBJECT and Household's icon is a house and
   * Learning's is an open book — `home` and `book` already draw both. `CHORE_KIND_GLYPH` in
   * `rules/choreKinds.ts` is what maps the six categories onto these, and it is the thing both apps read.
   */
  'person',
  'sun',
  'paw',
  'heart',
] as const;

export type GlyphKey = (typeof GLYPH_KEYS)[number];

/**
 * Retired subject names pointed at the glyph that replaced them. `handshake` and `hands-coin` were
 * commissioned and dropped — both reduce to mush at 36px — and anything already naming them resolves here.
 * They are NOT keys: there is one drawing per subject and no duplicates.
 */
export const GLYPH_ALIAS: Readonly<Record<string, GlyphKey>> = {
  handshake: 'key',
  'hands-coin': 'coin-return',
};

const KEYS: ReadonlySet<string> = new Set(GLYPH_KEYS);

/** Is this a name the build can draw — its own, or one that resolves to another? */
export function isGlyphKey(name: string): name is GlyphKey {
  return KEYS.has(name);
}

/**
 * The glyph a name resolves to, or `null` when nothing draws it.
 *
 * **A CHILD'S SCREEN AND AN AUTHOR'S SCREEN TREAT THE SAME `null` DIFFERENTLY, AND THAT IS THE POINT**
 * (Evan, 2026-09-21). On C14 a tile whose id names no glyph draws its own label as type, which is honest and
 * is why 95 of the 99 live tile ids render as words today. On O06 the same unknown key is REFUSED: a picture
 * silently standing in for the one the author meant is an error only the author could catch, and by the time
 * a child sees it nobody is looking. Same function, opposite policies — so the policy belongs at the call
 * site and this returns the fact.
 */
export function resolveGlyphKey(name: string): GlyphKey | null {
  if (isGlyphKey(name)) return name;
  return GLYPH_ALIAS[name] ?? null;
}
